import { StandardEventProperties } from '@betterment/js-runtime';
import { ToggleTip } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { Flex } from '@betterment/style-closet-react';

import Title from './Title';

import { trackClick } from '../../features/track';
import Bullet from '../../Shared/components/Bullet';
import { TextShimmerLoader } from '../../Shared/components/ShimmerLoaders';

type Props = {
  leadingTitle: string;
  trailingTitle?: string;
  subTitle?: string;
  strong?: boolean;
  bullet?: string;
  color?: string;
  loading?: boolean;
  projectionTooltip?: boolean;
};

const ProjectionTooltip: React.FC = () => (
  <ToggleTip>
    <ToggleTip.Trigger iconName="InfoCircle" label="Toggle Tip" />
    <ToggleTip.Content side="right" css={{ maxWidth: '300px' }}>
      Employer and bonus contributions are not factored into projections.
    </ToggleTip.Content>
  </ToggleTip>
);

const BulletedListItem: React.FC<{
  leadingTitle: string;
  trailingTitle?: string;
  bullet?: string;
  loading?: boolean;
  projectionTooltip?: React.ReactNode;
}> = ({ leadingTitle, trailingTitle, bullet, loading, projectionTooltip }) => (
  <>
    <Bullet color={bullet}>
      <Title title={leadingTitle} />
      {projectionTooltip && (
        <>
          {' '}
          <ProjectionTooltip />
        </>
      )}
    </Bullet>
    {loading && <TextShimmerLoader width="80" numLines={1} />}
    {!loading && <Title title={trailingTitle} />}
  </>
);

export const BulletedListItemWithTrackableLink: React.FC<{
  leadingTitle: string;
  trackableLinkName: string;
  trackableLinkProperties: StandardEventProperties;
  trackableLinkUrl: string;
  bullet?: string;
  loading?: boolean;
}> = ({ leadingTitle, trackableLinkName, trackableLinkProperties, trackableLinkUrl, bullet, loading }) => (
  <Flex my="2" justifyContent="space-between">
    <Bullet color={bullet}>
      <Title title={leadingTitle} />
    </Bullet>
    {loading && <TextShimmerLoader width={80} numLines={1} />}
    {!loading && (
      <SambaAutoLink
        onClick={() => trackClick(trackableLinkProperties)}
        to={trackableLinkUrl}
        css={{ fontSize: 'fontSize.50', textDecoration: 'underline' }}
      >
        {trackableLinkName}
      </SambaAutoLink>
    )}
  </Flex>
);

const ListItem: React.FC<{
  leadingTitle: string;
  trailingTitle?: string;
  subTitle?: string;
  color?: string;
  strong?: boolean;
  loading?: boolean;
}> = ({ leadingTitle, subTitle, trailingTitle, color, strong, loading }) => (
  <>
    <div>
      <Title title={leadingTitle} color={color} strong={strong} />
      {subTitle && <div className="u-color-bmtGrey90 sc-body--static--12">{subTitle}</div>}
    </div>
    {loading && <TextShimmerLoader width={80} numLines={1} />}
    {!loading && <Title title={trailingTitle} color={color} strong={strong} />}
  </>
);

const YtdListItem: React.FC<React.PropsWithChildren<Props>> = ({
  leadingTitle,
  trailingTitle,
  subTitle,
  loading = false,
  strong = false,
  projectionTooltip = false,
  bullet,
  color,
}) => {
  return (
    <Flex my="2" justifyContent="space-between">
      {bullet ? (
        <BulletedListItem
          leadingTitle={leadingTitle}
          trailingTitle={trailingTitle}
          bullet={bullet}
          loading={loading}
          projectionTooltip={projectionTooltip}
        />
      ) : (
        <ListItem
          leadingTitle={leadingTitle}
          trailingTitle={trailingTitle}
          subTitle={subTitle}
          strong={strong}
          loading={loading}
          color={color}
        />
      )}
    </Flex>
  );
};

export default YtdListItem;
